import { TypographyStylesProvider } from '@mantine/core'
import DOMPurify from 'dompurify'

import {
  EmploymentTypeLabels,
  JobListingType,
} from 'src/graphql/types/jobListings'
import {
  EndorsementDescriptions,
  LicenseClass,
} from 'src/graphql/types/licenses'

import {
  formatLicenseClasses,
  formatLocation,
  formatMinExperience,
  formatPay,
  hasCompensationInfo,
  hasRequirementsInfo,
  ShortFormJobListing,
} from './jobInfo.utils'

const SectionLabel = ({ children }) => (
  <div className="text-[11px] font-bold uppercase tracking-widest text-doubleNickel-gray-500">
    {children}
  </div>
)

const FieldRow = ({ label, children }) => (
  <div className="grid grid-cols-[110px_1fr] items-baseline gap-4 py-1.5 sm:grid-cols-[150px_1fr]">
    <span className="text-sm font-semibold text-doubleNickel-gray-500">
      {label}
    </span>
    <span className="text-sm leading-relaxed text-doubleNickel-gray-800 sm:text-base">
      {children}
    </span>
  </div>
)

const Chip = ({ children }) => (
  <span className="rounded-lg border border-doubleNickel-gray-200 bg-doubleNickel-gray-50 px-2.5 py-1 text-xs text-doubleNickel-gray-600 sm:text-sm">
    {children}
  </span>
)

export const JobHeaderChips = ({
  location,
  companyName,
}: {
  location?: string | null
  companyName?: string | null
}) => {
  if (!location && !companyName) {
    return null
  }
  return (
    <div className="flex flex-wrap gap-2">
      {location && (
        <span className="rounded-full border border-doubleNickel-gray-200 bg-doubleNickel-gray-50 px-3 py-1 text-xs font-semibold text-doubleNickel-gray-700 sm:text-sm">
          {location}
        </span>
      )}
      {companyName && (
        <span className="rounded-full border border-doubleNickel-gray-200 bg-doubleNickel-gray-50 px-3 py-1 text-xs font-semibold text-doubleNickel-gray-600 sm:text-sm">
          {companyName}
        </span>
      )}
    </div>
  )
}

interface JobInfoPanelProps {
  jobListing: ShortFormJobListing
  /**
   * Drop the panel's own card wrapper and render the sections bare, so a
   * caller can put the description and the interest form inside ONE card —
   * which is what the public job board does. Defaults to false, so
   * `ShortFormPage` keeps rendering its own card exactly as before.
   */
  unstyled?: boolean
}

const JobInfoPanel = ({ jobListing, unstyled = false }: JobInfoPanelProps) => {
  const location = formatLocation(jobListing.address)
  const pay = formatPay(jobListing)
  const licenseClasses = formatLicenseClasses(jobListing.licenseClass)
  // "No CDL required" must not carry the contradictory "Required" note
  const requiresCdl = (jobListing.licenseClass ?? []).some(
    (licenseClass) => licenseClass !== LicenseClass.NO_CDL
  )
  const minExperience = formatMinExperience(jobListing.minMonthsExperience)
  const showCompensation = hasCompensationInfo(jobListing)
  const showRequirements = hasRequirementsInfo(jobListing)
  // Office listings can carry requirements too (e.g. work history) —
  // only driver listings get the driver-specific label
  const requirementsLabel =
    jobListing.type === JobListingType.OFFICE
      ? 'Requirements'
      : 'Driver Requirements'
  // Only treat the description as HTML when it contains a recognizable tag —
  // plain text with angle brackets (e.g. "John<john@acme.com>") must not be
  // routed through the HTML path where the sanitizer would strip it
  const isHtmlDescription =
    /<(p|br|div|span|ul|ol|li|a|strong|em|b|i|u|h[1-6]|table|tr|td|th|blockquote|hr)\b[^>]*\/?>/i.test(
      jobListing.description ?? ''
    )

  const content = (
    <>
      {/* Header */}
      <div className="border-b border-doubleNickel-gray-100 pb-4 sm:pb-5">
        <h1 className="text-lg font-extrabold leading-snug text-doubleNickel-gray-900 sm:text-2xl">
          {jobListing.title}
        </h1>
        {jobListing.subtitle && (
          <p className="mt-1.5 text-sm leading-relaxed text-doubleNickel-gray-600 sm:text-base">
            {jobListing.subtitle}
          </p>
        )}
        {(location || jobListing.company?.companyName) && (
          <div className="mt-3">
            <JobHeaderChips
              location={location}
              companyName={jobListing.company?.companyName}
            />
          </div>
        )}
      </div>

      {/* Compensation */}
      {showCompensation && (
        <div className="border-b border-doubleNickel-gray-100 py-4 sm:py-5">
          <div className="mb-2">
            <SectionLabel>Compensation</SectionLabel>
          </div>
          {jobListing.employmentType && (
            <FieldRow label="Employment">
              {EmploymentTypeLabels[jobListing.employmentType]}
            </FieldRow>
          )}
          {pay && (
            <FieldRow label="Pay">
              <span className="font-bold">{pay}</span>
            </FieldRow>
          )}
          {jobListing.benefits && jobListing.benefits.length > 0 && (
            <FieldRow label="Benefits">
              <span className="flex flex-wrap gap-1.5">
                {jobListing.benefits.map((benefit) => (
                  <Chip key={benefit}>{benefit}</Chip>
                ))}
              </span>
            </FieldRow>
          )}
        </div>
      )}

      {/* Driver Requirements */}
      {showRequirements && (
        <div className="border-b border-doubleNickel-gray-100 py-4 sm:py-5">
          <div className="mb-2">
            <SectionLabel>{requirementsLabel}</SectionLabel>
          </div>
          {licenseClasses && (
            <FieldRow label="CDL class">
              {licenseClasses}
              {requiresCdl && (
                <span className="block text-xs text-doubleNickel-gray-500 sm:text-sm">
                  Required
                </span>
              )}
            </FieldRow>
          )}
          {jobListing.endorsements && jobListing.endorsements.length > 0 && (
            <FieldRow label="Endorsements">
              <span className="flex flex-wrap gap-1.5">
                {jobListing.endorsements.map((endorsement) => (
                  <Chip key={endorsement}>
                    {endorsement} — {EndorsementDescriptions[endorsement]}
                  </Chip>
                ))}
              </span>
            </FieldRow>
          )}
          {minExperience && (
            <FieldRow label="Min. experience">{minExperience}</FieldRow>
          )}
          {(jobListing.minEmploymentHistoryYears ?? 0) > 0 && (
            <FieldRow label="Work history">
              {jobListing.minEmploymentHistoryYears} year
              {jobListing.minEmploymentHistoryYears === 1 ? '' : 's'} of
              employment history
            </FieldRow>
          )}
        </div>
      )}

      {/* Description */}
      {jobListing.description && (
        <div className="pt-4 sm:pt-5">
          <div className="mb-2">
            <SectionLabel>Description</SectionLabel>
          </div>
          {isHtmlDescription ? (
            // Indeed-synced listings store rich HTML descriptions. This page
            // is public and unauthenticated, so sanitize before injecting.
            <TypographyStylesProvider className="text-sm leading-relaxed text-doubleNickel-gray-600 sm:text-[15px] [&_li]:my-0.5 [&_p]:mb-2 [&_ul]:pl-5">
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(jobListing.description),
                }}
              />
            </TypographyStylesProvider>
          ) : (
            <p className="whitespace-pre-line text-sm leading-relaxed text-doubleNickel-gray-600 sm:text-[15px]">
              {jobListing.description}
            </p>
          )}
        </div>
      )}
    </>
  )

  if (unstyled) {
    return content
  }

  return (
    <section className="rounded-xl border border-doubleNickel-gray-200 bg-white p-4 sm:p-8">
      {content}
    </section>
  )
}

export default JobInfoPanel
